import { useState, useCallback } from 'react';

const initialBookingState = {
  route: '',
  pickup: '',
  dropoff: '',
  date: null,
  schedule: '',
  riders: '',
  luggage: '',
  requestAccessibleVehicle: false,
  contactNumber: '',
  specialRequests: '',
  entityVerificationValue: ''
};

/**
 * useBookingFormState Hook
 *
 * Centralized form state management for booking creation.
 * Manages all form fields, UI state, and provides update helpers.
 *
 * State Ownership:
 * - Owns: booking object (all form fields)
 * - Owns: UI state (status, isWaitlisted, submitting)
 * - Owns: validation state (verificationError)
 *
 * @returns {Object} Form state and helpers
 * @returns {Object} .booking - Booking form data object
 * @returns {Function} .setBooking - Set booking state
 * @returns {Function} .updateField - Update single booking field
 * @returns {Function} .resetBooking - Reset booking to initial state
 * @returns {string} .status - Booking status
 * @returns {Function} .setStatus - Set booking status
 * @returns {boolean} .isWaitlisted - Whether booking is waitlisted
 * @returns {Function} .setIsWaitlisted - Set waitlisted state
 * @returns {boolean} .submitting - Whether form is submitting
 * @returns {Function} .setSubmitting - Set submitting state
 * @returns {string} .verificationError - Verification error message
 * @returns {Function} .setVerificationError - Set verification error
 */
const useBookingFormState = () => {
  // Initial booking state
  // Form data state
  const [booking, setBooking] = useState(initialBookingState);

  // UI state
  const [status, setStatus] = useState('');
  const [isWaitlisted, setIsWaitlisted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  /**
   * Update a single field in booking object
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  const updateField = useCallback((field, value) => {
    setBooking(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  /**
   * Reset booking form to initial state
   */
  const resetBooking = useCallback(() => {
    setBooking(initialBookingState);
    setStatus('');
    setIsWaitlisted(false);
    setSubmitting(false);
    setVerificationError('');
  }, []);

  return {
    // Form data
    booking,
    setBooking,
    updateField,
    resetBooking,

    // UI state
    status,
    setStatus,
    isWaitlisted,
    setIsWaitlisted,
    submitting,
    setSubmitting,

    // Validation state
    verificationError,
    setVerificationError,
  };
};

export default useBookingFormState;
